"use client"

import { motion } from "framer-motion"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Send, CheckCircle, AlertCircle } from "lucide-react"
import { budgetOptions } from "@/features/home/data"
import { staggerContainer, fadeUp, scaleIn, sectionLabel } from "@/lib/motion"

const VP = { once: true, amount: 0.05 }

export function ContactSection() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    const formData = new FormData(e.currentTarget)
    const payload = {
      name: formData.get('name') as string,
      company: formData.get('company') as string,
      email: formData.get('email') as string,
      budget: formData.get('budget') as string,
      message: formData.get('message') as string,
    }

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error ?? 'Bir hata oluştu. Lütfen tekrar deneyin.')
        return
      }

      setIsSubmitted(true)
    } catch {
      setError('Bağlantı hatası. İnternet bağlantınızı kontrol edin.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="iletisim" className="relative py-16 sm:py-24 lg:py-32" aria-label="İletişim ve başvuru formu">
      <div className="brand-divider" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 max-w-6xl">
        <div className="max-w-4xl mx-auto">

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
            className="text-center mb-12"
          >
            <motion.span variants={sectionLabel} className="text-primary font-medium text-sm uppercase tracking-wider inline-block">
              Başvuru Yap
            </motion.span>
            <motion.h2 variants={fadeUp} className="font-[family-name:var(--font-heading)] text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-5 sm:mb-6 text-foreground">
              Projenizi <span className="text-gradient-brand">Geliştirelim</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground text-lg leading-relaxed max-w-2xl mx-auto">
              Projiniz hakkında bize bilgi verin. Ekibimiz en kısa sürede sizinle
              iletişime geçecek.
            </motion.p>
          </motion.div>

          <motion.div
            variants={scaleIn}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            <div className="glass hairline rounded-3xl p-5 sm:p-8 md:p-12">
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" aria-hidden="true" />
                  </div>
                  <h3 className="font-[family-name:var(--font-heading)] text-2xl font-bold text-foreground mb-3">
                    Başvurunuz Alındı!
                  </h3>
                  <p className="text-muted-foreground">
                    En kısa sürede sizinle iletişime geçeceğiz.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <label htmlFor="contact-name" className="text-sm font-medium text-foreground">
                        Ad Soyad <span className="text-destructive" aria-hidden="true">*</span>
                      </label>
                      <Input
                        id="contact-name"
                        name="name"
                        type="text"
                        placeholder="Adınız Soyadınız"
                        required
                        autoComplete="name"
                        className="bg-input/50 border-border focus:border-primary focus:ring-primary h-12 text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="contact-company" className="text-sm font-medium text-foreground">Şirket</label>
                      <Input
                        id="contact-company"
                        name="company"
                        type="text"
                        placeholder="Şirket Adı"
                        autoComplete="organization"
                        className="bg-input/50 border-border focus:border-primary focus:ring-primary h-12 text-base"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <label htmlFor="contact-email" className="text-sm font-medium text-foreground">
                        E-posta <span className="text-destructive" aria-hidden="true">*</span>
                      </label>
                      <Input
                        id="contact-email"
                        name="email"
                        type="email"
                        placeholder="ornek@email.com"
                        required
                        autoComplete="email"
                        className="bg-input/50 border-border focus:border-primary focus:ring-primary h-12 text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="contact-budget" className="text-sm font-medium text-foreground">Bütçe Aralığı</label>
                      <select
                        id="contact-budget"
                        name="budget"
                        className="w-full h-12 px-4 rounded-md bg-input/50 border border-border text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-base"
                      >
                        {budgetOptions.map((option) => (
                          <option key={option.value} value={option.value} className="bg-background text-foreground">
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="contact-message" className="text-sm font-medium text-foreground">
                      Proje Özeti <span className="text-destructive" aria-hidden="true">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      placeholder="Projeniz hakkında kısaca bilgi verin..."
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-md bg-input/50 border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all resize-none text-base"
                    />
                  </div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3"
                      role="alert"
                    >
                      <AlertCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
                      {error}
                    </motion.div>
                  )}

                  <motion.div whileTap={{ scale: 0.98 }}>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-14 text-lg font-medium hover:glow-accent transition-all disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          <span>Gönderiliyor...</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Projeyi Gönder
                          <Send className="w-5 h-5" aria-hidden="true" />
                        </span>
                      )}
                    </Button>
                  </motion.div>

                  <p className="text-center text-xs text-muted-foreground">
                    <span className="text-destructive" aria-hidden="true">*</span> ile işaretli alanlar zorunludur
                  </p>
                </form>
              )}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  )
}
